import { useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { getAllClients, displayName, statusCounts } from '../data/mockClients'
import { navigate } from '../router/hash'
import { AppShell } from '../layout/AppShell'
import { Avatar } from '../components/Avatar'
import { Icon, type IconName } from '../components/Icon'
import { statusBadgeClass } from '../data/nav'

type Stat = {
  label: string
  value: number
  hint: string
  icon: IconName
  variant: string
}

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

const QUICK_ACTIONS: { label: string; hint: string; icon: IconName; path: string }[] = [
  { label: 'Clients', hint: 'Browse the roster', icon: 'users', path: '/clients' },
  { label: 'Transport', hint: 'Routes & pickups', icon: 'transport', path: '/modules/transport' },
  { label: 'Clinical', hint: 'Charts & notes', icon: 'clinical', path: '/modules/clinical' },
  { label: 'Activities', hint: "Today's programs", icon: 'activities', path: '/modules/activities' },
  { label: 'Billing', hint: 'Claims & invoices', icon: 'billing', path: '/modules/billing' },
  { label: 'Reports', hint: 'Analytics', icon: 'reports', path: '/modules/reports' },
]

export function DashboardPage({ currentPath }: { currentPath: string }) {
  const { user } = useAuth()
  const counts = statusCounts()
  const [query, setQuery] = useState('')

  const onSearch = (value: string) => {
    setQuery(value)
    if (value.trim()) navigate(`/clients?q=${encodeURIComponent(value.trim())}`)
  }

  const stats: Stat[] = [
    { label: 'Total clients', value: counts.all, hint: 'Across all statuses', icon: 'users', variant: 'stat--brand' },
    { label: 'Approved', value: counts.approved, hint: 'Active in program', icon: 'check', variant: 'stat--brand' },
    { label: 'Visitors', value: counts.visitors, hint: 'Trial / assessment', icon: 'user', variant: 'stat--info' },
    { label: 'On hold', value: counts.hold, hint: 'Needs follow-up', icon: 'clock', variant: 'stat--warn' },
  ]

  const recent = getAllClients().slice(0, 6)
  const name = user?.displayName || 'there'

  return (
    <AppShell
      currentPath={currentPath}
      title="Dashboard"
      subtitle="Center overview"
      search={{ value: query, onInput: onSearch, placeholder: 'Search clients…' }}
    >
    <div className="container">
      <div className="section-title">
        <div>
          <h2 style={{ fontSize: '1.35rem' }}>
            {greeting()}, {name} 👋
          </h2>
          <p className="muted" style={{ margin: '.25rem 0 0' }}>
            Here's what's happening across your center today.
          </p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => navigate('/clients')}>
          <Icon name="users" /> View clients
        </button>
      </div>

      <div className="stat-grid">
        {stats.map((s) => (
          <div key={s.label} className={`stat ${s.variant}`}>
            <div className="stat__icon">
              <Icon name={s.icon} />
            </div>
            <span className="stat__label">{s.label}</span>
            <span className="stat__value">{s.value}</span>
            <span className="stat__hint">{s.hint}</span>
          </div>
        ))}
      </div>

      <div className="dash-grid">
        <div className="card">
          <div className="card__head">
            <h3>Recent clients</h3>
            <div style={{ flex: '1' }} />
            <button type="button" className="btn btn--subtle btn--sm" onClick={() => navigate('/clients')}>
              See all
            </button>
          </div>
          <div className="table-wrap" style={{ border: '0', boxShadow: 'none', borderRadius: '0' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Phone</th>
                  <th>DOB</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/clients/${c.id}/overview`)}>
                    <td>
                      <div className="cell-client">
                        <Avatar client={c} />
                        <div>
                          <b>{displayName(c)}</b>
                          <small>ID {c.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={statusBadgeClass(c.status)}>{c.status}</span>
                    </td>
                    <td className="muted">{c.phoneCell || c.phoneHome || '—'}</td>
                    <td className="muted">{c.dob || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card__head">
            <h3>Quick actions</h3>
          </div>
          <div className="card__body">
            <div className="tile-grid">
              {QUICK_ACTIONS.map((a) => (
                <a
                  key={a.label}
                  className="tile"
                  href={`#${a.path}`}
                  onClick={(e) => {
                    e.preventDefault()
                    navigate(a.path)
                  }}
                >
                  <div className="tile__ic">
                    <Icon name={a.icon} />
                  </div>
                  <b>{a.label}</b>
                  <span>{a.hint}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </AppShell>
  )
}
